import { Show, createEffect, createMemo, createSignal, onCleanup, onMount, untrack } from "solid-js";
import { createDiffWorker } from "@fastdiff/worker";
import { createHighlightWorker, detectLang, registerLang } from "@fastdiff/highlight";
import {
  buildRows,
  compileMatcher,
  computeNavHunks,
  createDiffStore,
  createSearchStore,
  createViewStore,
  createHighlightStore,
  DropZone,
  FastdiffLogo,
  findMatches,
  HamburgerMenu,
  HunkNav,
  SearchBar,
  SplitView,
  StatusBar,
  ThemeProvider,
  type CompiledMatcher,
  type DropZoneMode,
  type FileSourceData,
  type Match,
  type NavHunk,
} from "@fastdiff/ui";

export default function App() {
  const diffWorker = createDiffWorker();
  const hlWorker = createHighlightWorker();
  const view = createViewStore();
  const search = createSearchStore();
  const [wrapLayoutMs, setWrapLayoutMs] = createSignal<number | null>(null);
  const [minimapMs, setMinimapMs] = createSignal<number | null>(null);
  const [searchMs, setSearchMs] = createSignal<number>(0);
  // Paste tab state — kept on the App so DropZone unmount (going to "ready"
  // and back via the home button) doesn't lose the user's pasted text.
  const [pasteLeft, setPasteLeft] = createSignal<string>("");
  const [pasteRight, setPasteRight] = createSignal<string>("");
  const [pasteMode, setPasteMode] = createSignal<DropZoneMode>("file");
  const store = createDiffStore(diffWorker);
  const highlight = createHighlightStore({
    tokenize: (text, lang, theme, signal) => hlWorker.tokenize(text, lang, theme, signal),
  });

  // Eagerly discover compiled DSL grammars (Vite resolves these at build/dev start).
  const generatedGrammars = import.meta.glob(
    "../../../languages-generated/*.tmLanguage.json",
    { eager: true },
  ) as Record<string, { default: { name: string; fileTypes?: string[] } }>;

  // Warm up highlight worker eagerly so first tokenize is fast,
  // then register every generated DSL grammar.
  hlWorker.warmup("fastdiff-light")
    .then(async () => {
      let lastMs = 0;
      for (const mod of Object.values(generatedGrammars)) {
        const g = (mod as { default: { name: string; fileTypes?: string[] } }).default;
        const extensions = (g.fileTypes ?? []).map((ft) => `.${ft}`);
        try {
          registerLang(g.name, extensions);
          const res = await hlWorker.registerGrammar(g, extensions);
          lastMs = res.timingMs;
        } catch {
          // eslint-disable-next-line no-console
          console.warn(`Failed to register grammar ${g.name}`);
        }
      }
      if (lastMs > 0) highlight.setGrammarLoadMs(lastMs);
    })
    .catch(() => { /* non-fatal */ });

  onCleanup(() => {
    diffWorker.terminate();
    hlWorker.terminate();
  });

  // When diff becomes ready, dispatch tokenize.
  createEffect(() => {
    const snap = store.snapshot();
    if (!snap) {
      highlight.reset();
      return;
    }
    if (!view.syntaxHighlight()) {
      highlight.reset();
      return;
    }
    const langL = detectLang(snap.model.meta.leftName);
    const langR = detectLang(snap.model.meta.rightName);
    // Read theme without tracking — tokens are theme-independent (colors come
    // from CSS variables), so a theme switch must NOT trigger re-tokenize.
    const theme = untrack(() => view.theme()) === "dark" ? "fastdiff-dark" : "fastdiff-light";
    void highlight.tokenize(
      snap.leftLines.toString(),
      snap.rightLines.toString(),
      langL,
      langR,
      theme,
    ).catch(() => { /* error already in store.status */ });
  });

  // --- Hunk navigation ---
  const [currentHunkIndex, setCurrentHunkIndex] = createSignal(0);

  const navHunks = createMemo<readonly NavHunk[]>(() => {
    const snap = store.snapshot();
    if (!snap) return [];
    return computeNavHunks(snap.model, snap.leftLines, snap.rightLines);
  });

  // snapshot 切替で 0 にリセット
  createEffect(() => {
    store.snapshot();
    setCurrentHunkIndex(0);
  });

  // Reset wrap layout timing when wrap is disabled or snapshot changes.
  createEffect(() => {
    if (!view.wrap()) setWrapLayoutMs(null);
  });
  createEffect(() => {
    store.snapshot();
    setWrapLayoutMs(null);
    setMinimapMs(null);
  });

  const nextHunk = () => {
    const total = navHunks().length;
    if (total > 0) setCurrentHunkIndex((i) => (i + 1) % total);
  };
  const prevHunk = () => {
    const total = navHunks().length;
    if (total > 0) setCurrentHunkIndex((i) => (i - 1 + total) % total);
  };

  // --- Search ---
  const matcher = createMemo<CompiledMatcher>(() =>
    compileMatcher(search.debouncedQuery(), {
      caseSensitive: search.caseSensitive(),
      regex: search.regex(),
      wholeWord: search.wholeWord(),
    })
  );

  const searchError = createMemo<string | null>(() => {
    const m = matcher();
    return m.ok ? null : m.error;
  });

  const matches = createMemo<readonly Match[]>(() => {
    const snap = store.snapshot();
    const q = search.debouncedQuery();
    if (!snap || q.length === 0) { setSearchMs(0); return []; }
    const rows = buildRows(snap.model, snap.leftLines, snap.rightLines);
    const t0 = performance.now();
    const out = findMatches(rows, matcher());
    setSearchMs(performance.now() - t0);
    return out;
  });

  // Feed total to store so currentIndex clamps correctly.
  createEffect(() => search.setTotal(matches().length));

  // Reset search state on snapshot switch.
  createEffect(() => {
    store.snapshot();
    search.resetForSnapshot();
  });

  let searchInputEl: HTMLInputElement | undefined;

  function isEditingTarget(t: EventTarget | null): boolean {
    if (!(t instanceof HTMLElement)) return false;
    return t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.isContentEditable;
  }

  onMount(() => {
    const handler = (e: KeyboardEvent) => {
      // Cmd/Ctrl+F: open search
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === "f") {
        e.preventDefault();
        search.open();
        queueMicrotask(() => {
          searchInputEl?.focus();
          searchInputEl?.select();
        });
        return;
      }
      // Esc when search is open: close it
      if (e.key === "Escape" && search.isOpen()) {
        e.preventDefault();
        search.close();
        return;
      }
      if (isEditingTarget(e.target)) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      if (k === "j") {
        nextHunk();
        e.preventDefault();
      } else if (k === "k") {
        prevHunk();
        e.preventDefault();
      } else if (search.isOpen() && k === "n") {
        if (e.shiftKey) search.prev();
        else search.next();
        e.preventDefault();
      }
    };
    window.addEventListener("keydown", handler);
    onCleanup(() => window.removeEventListener("keydown", handler));
  });

  const onSubmit = async (l: FileSourceData, r: FileSourceData) => {
    await store.load(l, r);
  };

  return (
    <ThemeProvider view={view}>
      <div class="fd-app">
        <div class="fd-topbar">
          <div class="fd-topbar-left">
            <Show when={store.status() !== "ready"}>
              <FastdiffLogo />
            </Show>
            <Show when={store.status() === "ready"}>
              <button
                ref={(el) => {
                  // vite-plugin-solid dev transform drops non-ASCII string
                  // literals on attributes; assign via setAttribute so the
                  // accessible name survives in both dev and prod.
                  el.setAttribute("aria-label", "ホームに戻る");
                  el.setAttribute("title", "ホームに戻る");
                }}
                class="fd-button-secondary fd-button-icon"
                onClick={() => store.reset()}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  aria-hidden="true"
                >
                  <path d="M3 11.5 12 4l9 7.5" />
                  <path d="M5 10v9.5a.5.5 0 0 0 .5.5H9v-6h6v6h3.5a.5.5 0 0 0 .5-.5V10" />
                </svg>
              </button>
            </Show>
          </div>
          <div class="fd-topbar-center">
            <Show when={store.status() === "ready"}>
              <HunkNav
                currentIndex={currentHunkIndex}
                total={() => navHunks().length}
                onPrev={prevHunk}
                onNext={nextHunk}
              />
            </Show>
            <Show when={store.status() === "ready" && search.isOpen()}>
              <SearchBar
                query={search.query}
                caseSensitive={search.caseSensitive}
                regex={search.regex}
                wholeWord={search.wholeWord}
                currentIndex={search.currentIndex}
                total={() => matches().length}
                error={searchError}
                onQueryChange={search.setQuery}
                onToggleCase={search.toggleCase}
                onToggleRegex={search.toggleRegex}
                onToggleWholeWord={search.toggleWholeWord}
                onNext={search.next}
                onPrev={search.prev}
                onClose={search.close}
                onInputRef={(el) => { searchInputEl = el; }}
              />
            </Show>
          </div>
          <div class="fd-topbar-right">
            <HamburgerMenu view={view} />
          </div>
        </div>

        <Show when={store.status() === "loading"}>
          <div class="fd-loading">差分を計算中…</div>
        </Show>

        <Show when={store.status() === "error"}>
          <div class="fd-error">
            <p>{store.error()}</p>
            <button class="fd-button-primary" onClick={() => store.reset()}>やり直す</button>
          </div>
        </Show>

        <Show when={store.status() === "empty"}>
          <DropZone
            onSubmit={onSubmit}
            pasteLeft={pasteLeft}
            pasteRight={pasteRight}
            mode={pasteMode}
            onPasteLeftChange={setPasteLeft}
            onPasteRightChange={setPasteRight}
            onModeChange={setPasteMode}
          />
        </Show>

        <Show when={store.status() === "ready" && store.snapshot()}>
          {(snap) => (
            <SplitView
              model={snap().model}
              leftLines={snap().leftLines}
              rightLines={snap().rightLines}
              leftTokens={highlight.left() ?? undefined}
              rightTokens={highlight.right() ?? undefined}
              leftLang={highlight.leftLang()}
              rightLang={highlight.rightLang()}
              wrap={view.wrap()}
              fontSize={view.fontSize()}
              highlighting={highlight.status() === "loading"}
              navHunks={navHunks()}
              currentHunkIndex={currentHunkIndex()}
              matches={search.isOpen() ? matches() : []}
              currentMatchIndex={search.currentIndex()}
              minimap={view.minimap()}
              onTimingMs={(key, ms) => {
                if (key === "buildRows") store.setBuildRowsMs(ms);
                else if (key === "wrapLayout") setWrapLayoutMs(ms);
                else if (key === "minimap") setMinimapMs(ms);
              }}
            />
          )}
        </Show>

        <Show when={store.status() === "ready"}>
          <StatusBar
            decodeMs={() => store.timing()?.decodeMs ?? null}
            diffMs={() => store.timing()?.diffMs ?? null}
            buildRowsMs={() => store.timing()?.buildRowsMs ?? null}
            highlightMs={() => highlight.timing()?.maxMs ?? null}
            wrapLayoutMs={wrapLayoutMs}
            minimapMs={minimapMs}
            searchMs={() => search.isOpen() ? searchMs() : null}
            grammarLoadMs={highlight.grammarLoadMs}
            detailed={view.showDetailedTimings}
            highlightDisabled={() => !view.syntaxHighlight()}
          />
        </Show>
      </div>
    </ThemeProvider>
  );
}
